import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** Artyeto: id of the latest cart update, so an older count never wins. */
  #countRequest = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    // Artyeto: announce frames only; lens lines are nested under them.
    const request = ++this.#countRequest;

    event.promise
      ?.then(({ cart, detail }) => topLevelCount(event, detail, cart?.totalQuantity))
      .then((cartCount) => {
        if (cartCount === undefined || request !== this.#countRequest) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

/**
 * Artyeto: sums the quantities of top-level cart lines. Nested lines (lenses added with `parent_id`)
 * come back from the Ajax API with `parent_relationship: { parent_key }`.
 * @param {unknown} items - Cart items as returned by `/cart.js`.
 * @returns {number | undefined} Undefined when `items` is not a list, so callers can fall back.
 */
function countTopLevelItems(items) {
  if (!Array.isArray(items)) return undefined;

  return items.reduce((total, item) => {
    if (item?.parent_relationship?.parent_key) return total;
    return total + (Number(item?.quantity) || 0);
  }, 0);
}

/**
 * Artyeto: top-level count for a cart update. Every dispatcher in this theme resolves with the full
 * `/cart.js` items in `detail.items`. For any other dispatcher (apps, `Shopify.actions`), `/cart.js`
 * is fetched once per event and shared with `cart-icon.js` through the event object; the total
 * quantity (which includes lens lines) is the last resort.
 * Keep in sync with the copy in `cart-icon.js`.
 * @param {Event} event
 * @param {{ items?: unknown } | undefined} detail
 * @param {number | undefined} fallback - Count used when `/cart.js` fails.
 * @returns {Promise<number | undefined>}
 */
function topLevelCount(event, detail, fallback) {
  const fromDetail = countTopLevelItems(detail?.items);
  if (fromDetail !== undefined) return Promise.resolve(fromDetail);
  if (fallback === 0) return Promise.resolve(0);

  const shared = /** @type {Event & { artyetoTopLevelCount?: Promise<number | undefined> }} */ (event);
  shared.artyetoTopLevelCount ??= fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => countTopLevelItems(data?.items))
    .catch(() => undefined);

  return shared.artyetoTopLevelCount.then((count) => count ?? fallback);
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
